import React from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { PageSkeleton, PanelSkeleton, TileRowSkeleton } from '@/components/ui/calm';

/**
 * The loading kit: shapes in the size of what arrives, so a page lands into
 * its own outline instead of jumping by a screen when a centred spinner is
 * replaced. DESIGN.md s22: skeletons, never large spinners; shapes close to
 * the final layout. Tile row and panel live in calm.jsx (re-exported here);
 * this adds the table, the chart and the whole page frame.
 */
export { PageSkeleton, PanelSkeleton, TileRowSkeleton };

/** A table-kit table: header band and rows at the kit's row height. */
export function TableSkeleton({ rows = 8, cols = 5, className }) {
  return (
    <div className={cn('overflow-hidden rounded-lg border border-[hsl(var(--card-border))] bg-card', className)} aria-hidden>
      <div className="flex gap-6 border-b border-border bg-muted/40 px-5 py-3">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className={cn('h-3.5', i === 0 ? 'w-40' : 'w-20', i > 0 && 'ml-auto')} />
        ))}
      </div>
      <div className="divide-y divide-border">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-6 px-5 py-3">
            {Array.from({ length: cols }).map((__, j) => (
              <Skeleton key={j} className={cn('h-4', j === 0 ? 'w-48' : 'w-16', j > 0 && 'ml-auto')} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** A chart card: title lines and a plot area of the chart's height. */
export function ChartSkeleton({ height = 260, className }) {
  return (
    <div className={cn('rounded-lg border border-[hsl(var(--card-border))] bg-card p-6', className)} aria-hidden>
      <Skeleton className="h-3.5 w-28" />
      <Skeleton className="mt-3 h-5 w-64 max-w-full" />
      <Skeleton className="mt-6 w-full rounded-lg" style={{ height }} />
    </div>
  );
}

/** The report body while its first answer loads: tiles, a chart, a table. */
export function ReportSkeleton({ tiles = 4, chart = 220, rows = 6, className, status = true }) {
  return (
    <div className={cn('space-y-5', className)} {...(status ? { role: 'status', 'aria-label': 'Loading' } : { 'aria-hidden': true })}>
      {tiles > 0 && <TileRowSkeleton n={tiles} />}
      {chart > 0 && <ChartSkeleton height={chart} />}
      {rows > 0 && <TableSkeleton rows={rows} />}
    </div>
  );
}

/**
 * A whole page in outline, for the moment before a page's code has arrived:
 * the header at PageHeader's padding (title, one line of description) over
 * the report body at PageContent's. Same widths and gutters, so when the page
 * renders, its title and tiles land where their outlines were.
 */
export function PageFrameSkeleton() {
  return (
    <div role="status" aria-label="Loading">
      <div className="mx-auto w-full max-w-[1440px] px-4 pb-4 pt-6 sm:px-8 sm:pb-5 sm:pt-7" aria-hidden>
        <Skeleton className="h-8 w-64 max-w-full" />
        <Skeleton className="mt-3 h-4 w-[min(560px,90%)]" />
      </div>
      <div className="mx-auto w-full max-w-[1440px] px-4 pb-8 pt-1 sm:px-8 sm:pb-10 sm:pt-2">
        <ReportSkeleton status={false} />
      </div>
    </div>
  );
}
